import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTTS } from 'hooks/useTTS';

type RegisteredBlock = {
    id: string;
    order: number;
    getText: () => string;
};

type TTSContextValue = {
    enabled: boolean;
    activeBlockId: string | null;
    rate: number;
    isSpeaking: boolean;
    isPaused: boolean;
    registerBlock: (block: RegisteredBlock) => () => void;
    speakBlock: (id: string) => void;
    speakAll: () => void;
    next: () => void;
    prev: () => void;
    playPause: () => void;
    stop: () => void;
    setRate: (n: number) => void;
};

const TTSContextInstance = createContext<TTSContextValue | null>(null);

type TTSProviderProps = {
    enabled: boolean;
    children: React.ReactNode;
    /** Incrementing key (e.g., question index) that clears registered blocks + stops speech. */
    resetKey?: string | number;
};

export function TTSProvider({ enabled, children, resetKey }: TTSProviderProps) {
    const { rate, isSpeaking, isPaused, speak, pause, resume, stop, setRate, setOnComplete } = useTTS();
    const [activeBlockId, setActiveBlockId] = useState<string | null>(null);
    const blocksRef = useRef<Map<string, RegisteredBlock>>(new Map());
    const activeIdRef = useRef<string | null>(null);
    const chainingAllRef = useRef<boolean>(false);

    useEffect(() => {
        activeIdRef.current = activeBlockId;
    }, [activeBlockId]);

    useEffect(() => {
        stop();
        setActiveBlockId(null);
        chainingAllRef.current = false;
    }, [resetKey, stop]);

    const getOrderedIds = useCallback((): string[] => {
        return Array.from(blocksRef.current.values())
            .sort((a, b) => a.order - b.order)
            .map(b => b.id);
    }, []);

    const registerBlock = useCallback((block: RegisteredBlock) => {
        blocksRef.current.set(block.id, block);
        return () => {
            blocksRef.current.delete(block.id);
        };
    }, []);

    const speakBlock = useCallback((id: string) => {
        const block = blocksRef.current.get(id);
        if (!block) return;
        chainingAllRef.current = false;
        setActiveBlockId(id);
        speak(block.getText());
    }, [speak]);

    const speakAll = useCallback(() => {
        const ids = getOrderedIds();
        if (!ids.length) return;
        chainingAllRef.current = true;
        setActiveBlockId(ids[0]);
        const block = blocksRef.current.get(ids[0]);
        if (block) {
            speak(block.getText());
        }
    }, [getOrderedIds, speak]);

    const next = useCallback(() => {
        const ids = getOrderedIds();
        if (!ids.length) return;
        const currentIndex = activeIdRef.current ? ids.indexOf(activeIdRef.current) : -1;
        const nextIndex = currentIndex + 1 >= ids.length ? 0 : currentIndex + 1;
        speakBlock(ids[nextIndex]);
    }, [getOrderedIds, speakBlock]);

    const prev = useCallback(() => {
        const ids = getOrderedIds();
        if (!ids.length) return;
        const currentIndex = activeIdRef.current ? ids.indexOf(activeIdRef.current) : 0;
        if (currentIndex <= 0) return;
        speakBlock(ids[currentIndex - 1]);
    }, [getOrderedIds, speakBlock]);

    const playPause = useCallback(() => {
        if (isSpeaking) {
            pause();
        } else if (isPaused) {
            resume();
        } else {
            speakAll();
        }
    }, [isSpeaking, isPaused, pause, resume, speakAll]);

    const advanceChain = useCallback(() => {
        if (!chainingAllRef.current) return;
        const ids = getOrderedIds();
        if (!ids.length || !activeIdRef.current) return;
        const idx = ids.indexOf(activeIdRef.current);
        if (idx === -1 || idx === ids.length - 1) {
            chainingAllRef.current = false;
            return;
        }
        const nextId = ids[idx + 1];
        const block = blocksRef.current.get(nextId);
        if (!block) {
            chainingAllRef.current = false;
            return;
        }
        setActiveBlockId(nextId);
        speak(block.getText());
    }, [getOrderedIds, speak]);

    useEffect(() => {
        setOnComplete(advanceChain);
        return () => setOnComplete(null);
    }, [advanceChain, setOnComplete]);

    const value = useMemo<TTSContextValue>(() => ({
        enabled,
        activeBlockId,
        rate,
        isSpeaking,
        isPaused,
        registerBlock,
        speakBlock,
        speakAll,
        next,
        prev,
        playPause,
        stop,
        setRate
    }), [enabled, activeBlockId, rate, isSpeaking, isPaused, registerBlock, speakBlock, speakAll, next, prev, playPause, stop, setRate]);

    return (
        <TTSContextInstance.Provider value={value}>
            {children}
        </TTSContextInstance.Provider>
    );
}

export function useTTSContext(): TTSContextValue {
    const ctx = useContext(TTSContextInstance);
    if (!ctx) {
        throw new Error('useTTSContext must be used within a TTSProvider');
    }
    return ctx;
}

export function useOptionalTTSContext(): TTSContextValue | null {
    return useContext(TTSContextInstance);
}
